import ChartFrame from './ChartFrame'
import DataTable from '../ui/DataTable'
import { MAX_POLLS, POLL_DELAYS } from '../hooks/useLeakTest'
import styles from './DiscoveryTimeline.module.css'

interface DiscoveryTimelineProps {
    /** Resolver address -> the poll index it was first observed at. */
    firstSeen: Map<string, number>
}

/** Cumulative elapsed time at each poll, for the axis labels. */
const ELAPSED_AT = POLL_DELAYS.reduce<number[]>((acc, d, i) => {
    acc.push((acc[i - 1] ?? 0) + d)
    return acc
}, [])

/**
 * When each resolver was first *observed*.
 *
 * The API returns no timestamps -- api_dnsecho_check tallies addresses with no
 * time field -- so this axis is client poll time, not the moment the query was
 * actually made. It is titled and captioned accordingly rather than implying a
 * precision the data does not have. A dot, not a bar: a bar would suggest the
 * resolver was active for a span, which we do not know.
 */
export default function DiscoveryTimeline({ firstSeen }: DiscoveryTimelineProps) {
    const entries = [...firstSeen.entries()].sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
    const late = entries.filter(([, poll]) => poll >= 5)

    return (
        <ChartFrame
            title="First seen"
            note="Observed at poll time, not query time — the server records which resolvers asked, not when."
            aside={`over ${(ELAPSED_AT[MAX_POLLS - 1] / 1000).toFixed(1)}s`}
            table={
                <DataTable
                    caption="Poll at which each resolver was first observed"
                    columns={[
                        { key: 'ip', header: 'Resolver', render: ([ip]: [string, number]) => ip },
                        { key: 'poll', header: 'Poll', numeric: true,
                          render: ([, p]: [string, number]) => p + 1 },
                        { key: 'at', header: 'Elapsed', numeric: true,
                          render: ([, p]: [string, number]) => `${(ELAPSED_AT[p] / 1000).toFixed(1)}s` },
                    ]}
                    rows={entries}
                    rowKey={([ip]) => ip}
                />
            }
        >
            <ul className={styles.list}>
                {entries.map(([ip, poll]) => (
                    <li key={ip} className={styles.row}>
                        <span className={styles.label}>{ip}</span>
                        <span className={styles.track}>
                            <span
                                className={styles.dot}
                                style={{ left: `${(poll / (MAX_POLLS - 1)) * 100}%` }}
                            />
                        </span>
                        <span className={styles.at}>{(ELAPSED_AT[poll] / 1000).toFixed(1)}s</span>
                    </li>
                ))}
            </ul>

            <div className={styles.axis} aria-hidden="true">
                <span>0s</span>
                <span>{(ELAPSED_AT[MAX_POLLS - 1] / 1000).toFixed(1)}s</span>
            </div>

            {late.length > 0 && (
                <p className={styles.note}>
                    {late.length} resolver{late.length === 1 ? '' : 's'} appeared late in the test.
                    Short tests can miss these.
                </p>
            )}
        </ChartFrame>
    )
}
